import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import ClickableLocation from './ClickableLocation'; 

// Importar los iconos SVG
import { ReactComponent as CarIcon } from '../../assets/svg/maps/Icon_tractor.svg';
import { ReactComponent as SpeedIcon } from '../../assets/svg/maps/speed.svg';
import { ReactComponent as TimeIcon } from '../../assets/svg/components/calendar.svg';
import { ReactComponent as LocationIcon } from '../../assets/svg/maps/location.svg';
import { ReactComponent as ClipboardIcon } from '../../assets/svg/components/copy.svg';

const VehicleList = ({ vehicles, selectedVehicleId, onSelectVehicle, theme, isConnected, connectionStatus }) => {
  const { copyToClipboard } = useCopyToClipboard();
  const [updatedAddresses, setUpdatedAddresses] = useState(new Map());
  // 🔧 SOLUCIÓN: Generador de keys únicas consistente con VehicleMarkers
  const generateUniqueKey = (vehicle, index) => {
    const provider = vehicle.provider || vehicle.Provider || 'unknown';
    const vehicleId = vehicle.UnitId || vehicle.UnitPlate || vehicle.GpsIdentif || vehicle.Imei;
    const fallbackId = `${vehicle.Latitude}_${vehicle.Longitude}_${index}`;
    
    return `${provider}_${vehicleId || fallbackId}`;
  };

  // 🚀 OPTIMIZACIÓN: Procesar y deduplicar vehículos con useMemo
  const processedVehicles = useMemo(() => {
    if (!vehicles || vehicles.length === 0) return [];
    
    const vehicleMap = new Map();
    const validVehicles = [];

    vehicles.forEach((vehicle, index) => {
      const uniqueKey = generateUniqueKey(vehicle, index);
      
      // Evitar duplicados exactos (mismo proveedor + mismo vehículo)
      if (!vehicleMap.has(uniqueKey)) {
        vehicleMap.set(uniqueKey, {
          ...vehicle,
          _uniqueKey: uniqueKey,
          _originalIndex: index
        });
        validVehicles.push(vehicleMap.get(uniqueKey));
      } else {
        // Si existe, mantener el más reciente basado en ReportDate
        const existing = vehicleMap.get(uniqueKey);
        const existingDate = new Date(existing.ReportDate || existing.lastUpdate || 0);
        const currentDate = new Date(vehicle.ReportDate || vehicle.lastUpdate || 0);
        
        if (currentDate > existingDate) {
          const updatedVehicle = {
            ...vehicle,
            _uniqueKey: uniqueKey,
            _originalIndex: index
          };
          vehicleMap.set(uniqueKey, updatedVehicle);
          
          // Actualizar en el array
          const existingIndex = validVehicles.findIndex(v => v._uniqueKey === uniqueKey);
          if (existingIndex !== -1) {
            validVehicles[existingIndex] = updatedVehicle;
          }
        }
      }
    });
    return validVehicles;
  }, [vehicles]);

  // 🆕 NUEVO: Callback para manejar actualizaciones de direcciones
  const handleAddressUpdate = (updateData) => {
    const vehicleKey = updateData.vehicleId || `${updateData.coordinates.latitude}_${updateData.coordinates.longitude}`;
    
    setUpdatedAddresses(prev => new Map(prev).set(vehicleKey, {
      address: updateData.address,
      timestamp: Date.now(),
      source: updateData.source
    }));
  };

  // 🔧 MODIFICADO: Función para obtener dirección (con direcciones actualizadas)
  const getVehicleAddress = (vehicle) => {
    const vehicleId = vehicle.UnitId || vehicle.UnitPlate;
    const updated = updatedAddresses.get(vehicleId);
    
    // Si hay una dirección actualizada reciente (menos de 1 hora), usarla
    if (updated && (Date.now() - updated.timestamp) < 3600000) {
      return updated.address;
    }
    
    // Usar dirección original del vehículo
    return vehicle.Domicilio;
  };
  
  // Función para determinar el estado del vehículo
  const getVehicleStatus = (vehicle) => {
    if ((vehicle.GpsSpeed || 0) > 0) {
      return { label: 'En movimiento', className: 'moving' };
    } else if (vehicle.Ignition === 1) {
      return { label: 'Motor encendido', className: 'idle' };
    } else {
      return { label: 'Estacionado', className: 'parked' };
    }
  };

  // Función para calcular tiempo transcurrido - CORREGIDA
  const getTimeSinceLastUpdate = (date) => {
    if (!date) return 'Desconocido';
    
    // 🔧 NUEVO: Para fechas en tiempo real, usar directamente sin conversión de zona horaria
    const lastUpdate = new Date(date + (date.includes('Z') ? '' : 'Z')); // Agregar Z si no tiene zona horaria
    const now = new Date();
    const diffMs = now - lastUpdate;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Hace menos de un minuto';
    if (diffMins < 60) return `Hace ${diffMins} minutos`;
    if (diffMins < 1440) return `Hace ${Math.floor(diffMins / 60)} horas`;
    return `Hace ${Math.floor(diffMins / 1440)} días`;
  };

  // 🔧 NUEVA: Función para formatear fecha en tiempo real
  const formatRealTimeDate = (date) => {
    if (!date) return 'Fecha no disponible';
    
    try {
      // Para fechas en tiempo real, crear fecha directamente sin conversión
      const dateObj = new Date(date + (date.includes('Z') ? '' : 'Z'));
      
      // Formatear en zona horaria local pero manteniendo la hora original
      return dateObj.toLocaleString('es-CO', {
        timeZone: 'America/Bogota', // Ajusta según tu zona horaria
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      console.error('Error formateando fecha:', error);
      return 'Fecha inválida';
    }
  };
  
  // Función para copiar coordenadas
  const handleCopyCoordinates = (vehicle, event) => {
    event.stopPropagation();
    const lat = parseFloat(vehicle.Latitude);
    const lng = parseFloat(vehicle.Longitude);
    
    if (!isNaN(lat) && !isNaN(lng)) {
      const coordsText = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
      copyToClipboard(coordsText);
    }
  };

  // 🔧 SOLUCIÓN: Función mejorada para determinar vehículo seleccionado
  const isVehicleSelected = (vehicle) => {
    if (!selectedVehicleId) return false;
    
    const vehicleId = vehicle.UnitId || vehicle.UnitPlate;
    const provider = vehicle.provider || vehicle.Provider;
    
    // Comparar por ID directo o por key única
    return selectedVehicleId === vehicleId || 
           selectedVehicleId === vehicle._uniqueKey ||
           (selectedVehicleId.includes('_') && selectedVehicleId === `${provider}_${vehicleId}`);
  };

  if (processedVehicles.length === 0) {
    return (
      <div className={`GM__${theme}-empty-state`}>
        <h2 className={`GM__${theme}-empty-title`}>No hay vehículos disponibles</h2>
        {!isConnected && (
          <p className={`GM__${theme}-connection-warning`}>
            Sin conexión al servidor • {connectionStatus}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`GM__${theme}-vehicle-list-container`}>
      <div className={`GM__${theme}-vehicle-list-header`}>
        <h2 className={`GM__${theme}-vehicle-list-title`}>
          {processedVehicles.length} vehículos monitoreados
        </h2>
        
        {/* 🆕 Indicador de estado de conexión */}
        {!isConnected && (
          <div className={`GM__${theme}-connection-indicator offline`}>
            <span className={`GM__${theme}-connection-dot`}></span>
            <span>Datos en caché</span>
          </div>
        )}
      </div>
      
      <div className={`GM__${theme}-vehicle-list`}>
        {processedVehicles.map((vehicle) => {
          const vehicleId = vehicle.UnitId || vehicle.UnitPlate;
          const status = getVehicleStatus(vehicle);
          const uniqueKey = vehicle._uniqueKey;
          const currentAddress = getVehicleAddress(vehicle); // 🔧 Usar nueva función
          
          return (
            <div 
              key={uniqueKey}
              className={`GM__${theme}-vehicle-item ${isVehicleSelected(vehicle) ? 'selected' : ''}`}
              onClick={() => onSelectVehicle(vehicle)}
            >
              <div className={`GM__${theme}-vehicle-header`}>
                <div className={`GM__${theme}-vehicle-avatar`}>
                  <CarIcon className={`GM__${theme}-vehicle-icon`} />
                  
                  {/* 🆕 Badge de proveedor */}
                  {(vehicle.provider || vehicle.Provider) && (
                    <span className={`GM__${theme}-provider-badge-small`}>
                      {(vehicle.provider || vehicle.Provider).substring(0, 3).toUpperCase()}
                    </span>
                  )}
                </div>
                
                <div className={`GM__${theme}-vehicle-info`}>
                  <h3 className={`GM__${theme}-vehicle-name`}>
                    {vehicle.UnitId || 'Vehículo sin ID'}
                  </h3>
                  <p className={`GM__${theme}-vehicle-plate`}>
                    Placa: {vehicle.UnitPlate || 'N/A'}
                    {/* 🆕 Mostrar proveedor si está disponible */}
                    {(vehicle.provider || vehicle.Provider) && (
                      <span className={`GM__${theme}-provider-text`}>
                        • {vehicle.provider || vehicle.Provider}
                      </span>
                    )}
                  </p>
                </div>
                
                <div className={`GM__${theme}-vehicle-status-badge ${status.className}`}>
                  {status.label}
                </div>
              </div>
              
              <div className={`GM__${theme}-vehicle-details`}>
                <div className={`GM__${theme}-vehicle-detail`}>
                  <SpeedIcon className={`GM__${theme}-detail-icon`} />
                  <span>{vehicle.GpsSpeed || 0} km/h</span>
                </div>
                
                <div className={`GM__${theme}-vehicle-detail`}>
                  <TimeIcon className={`GM__${theme}-detail-icon`} />
                  <span>{getTimeSinceLastUpdate(vehicle.ReportDate)}</span>
                </div>
                
                {vehicle.Latitude && vehicle.Longitude && !isNaN(parseFloat(vehicle.Latitude)) && !isNaN(parseFloat(vehicle.Longitude)) && (
                  <div className={`GM__${theme}-vehicle-coordinates`}>
                    <span className={`GM__${theme}-coordinates-label`}>
                      {parseFloat(vehicle.Latitude).toFixed(6)}, {parseFloat(vehicle.Longitude).toFixed(6)}
                    </span>
                    <button 
                      onClick={(e) => handleCopyCoordinates(vehicle, e)}
                      className={`GM__${theme}-copy-button`}
                      title="Copiar coordenadas"
                    >
                      <ClipboardIcon className={`GM__${theme}-icon-small`} />
                    </button>
                  </div>
                )}
                
                <div className={`GM__${theme}-vehicle-location`}>
                  <ClickableLocation
                    latitude={vehicle.Latitude}
                    longitude={vehicle.Longitude}
                    initialAddress={currentAddress}
                    vehicleId={vehicleId}
                    theme={theme}
                    className={`GM__${theme}-vehicle-location-clickable`}
                    maxLength={35}
                    onAddressUpdate={handleAddressUpdate}
                  />
                  
                  {/* Botón de copia independiente si hay dirección */}
                  {currentAddress && currentAddress !== 'Ubicación no disponible' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(currentAddress);
                      }}
                      className={`GM__${theme}-copy-button`}
                      title="Copiar ubicación"
                    >
                      <ClipboardIcon className={`GM__${theme}-icon-small`} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

VehicleList.propTypes = {
  vehicles: PropTypes.array.isRequired,
  selectedVehicleId: PropTypes.string,
  onSelectVehicle: PropTypes.func.isRequired,
  theme: PropTypes.string.isRequired,
  isConnected: PropTypes.bool,
  connectionStatus: PropTypes.string
};

export default VehicleList;